/**
 * Route tree for the app.
 *
 * Layers, outside in:
 *   1. Public routes (/login, /signup, /forbidden, 404) — no auth.
 *   2. RequireAuth — anything authenticated sits under this.
 *   3. AppShell — the chrome (nav + header + outlet).
 *   4. RequireRole — per-area role gates; nested under shell so the
 *      forbidden path still renders inside the shell breadcrumb.
 *
 * The backend's auth middleware on every intelligence route is the final
 * authority. Route guards only shape the UX — they must never be the
 * only thing protecting a sensitive action.
 */
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { lazy, Suspense } from "react";

import { RequireAuth } from "@/components/auth/RequireAuth";
import { RequireRole } from "@/components/auth/RequireRole";
import { AppShell } from "@/components/layout/AppShell";

// Lazy-load the page components so the login bundle stays tiny.
const LoginPage = lazy(() => import("@/pages/Login"));
const SignUpPage = lazy(() => import("@/pages/SignUp"));
const CustomerHomePage = lazy(() => import("@/pages/CustomerHome"));
const AgentDesktopPage = lazy(() => import("@/pages/AgentDesktop"));
const ManagerDashboardPage = lazy(() => import("@/pages/ManagerDashboard"));
const OutreachPage = lazy(() => import("@/pages/Outreach"));
const TriggersPage = lazy(() => import("@/pages/Triggers"));
const AdminPage = lazy(() => import("@/pages/Admin"));
const ForbiddenPage = lazy(() => import("@/pages/Forbidden"));
const NotFoundPage = lazy(() => import("@/pages/NotFound"));

const routes = createBrowserRouter([
  { path: "/", element: <Navigate to="/home" replace /> },
  {
    path: "/login",
    element: (
      <Suspense fallback={null}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: "/signup",
    element: (
      <Suspense fallback={null}>
        <SignUpPage />
      </Suspense>
    ),
  },
  {
    path: "/forbidden",
    element: (
      <Suspense fallback={null}>
        <ForbiddenPage />
      </Suspense>
    ),
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            path: "/home",
            element: (
              <Suspense fallback={null}>
                <CustomerHomePage />
              </Suspense>
            ),
          },
          {
            element: <RequireRole allow={["agent"]} />,
            children: [
              {
                path: "/agent",
                element: (
                  <Suspense fallback={null}>
                    <AgentDesktopPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            element: <RequireRole allow={["manager"]} />,
            children: [
              {
                path: "/dashboard",
                element: (
                  <Suspense fallback={null}>
                    <ManagerDashboardPage />
                  </Suspense>
                ),
              },
              {
                path: "/outreach",
                element: (
                  <Suspense fallback={null}>
                    <OutreachPage />
                  </Suspense>
                ),
              },
              {
                path: "/triggers",
                element: (
                  <Suspense fallback={null}>
                    <TriggersPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            element: <RequireRole allow={["admin"]} />,
            children: [
              {
                path: "/admin",
                element: (
                  <Suspense fallback={null}>
                    <AdminPage />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: (
      <Suspense fallback={null}>
        <NotFoundPage />
      </Suspense>
    ),
  },
]);

export function AppRouter() {
  return <RouterProvider router={routes} />;
}
